import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle, Callout, Switch,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import apiClient from "../../utils/apiClient";
import { getWebsiteCheckoutSettings, updateWebsiteCheckoutSettings } from "../../utils/websiteOrdersApi";

/** The Razorpay webhook address, absolute — the API may live on another host. */
const webhookUrl = () => {
  try {
    return new URL(`${String(apiClient.defaults.baseURL || "/api").replace(/\/+$/, "")}/payments/webhook`, window.location.origin).href;
  } catch {
    return "/api/payments/webhook";
  }
};

/**
 * Settings → Payments → Payments on your website.
 *
 * The institute-wide switch for paying on the public website: a visitor picks
 * a course, pays (in full, or just the registration fee), and is enrolled at
 * once with a receipt and their login details by email. Each course opts in
 * too, in its own settings. Off by default.
 *
 * The server refuses to switch it on without a usable Razorpay account, and
 * this card says what is missing rather than letting that be a surprise.
 */
export default function WebsiteCheckoutSettings({ showToast }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    return getWebsiteCheckoutSettings()
      .then(({ data }) => setCfg(data.data))
      .catch(() => setLoadError(true));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (enabled) => {
    const previous = cfg;
    setCfg((c) => ({ ...c, enabled }));
    setBusy(true);
    try {
      const { data } = await updateWebsiteCheckoutSettings(enabled);
      setCfg(data.data);
      showToast?.(enabled ? "Website payments are on. Switch them on for each course you want to sell." : "Website payments are off.");
    } catch (err) {
      setCfg(previous);
      showToast?.(err?.response?.data?.message || "Couldn't save the website payments setting.", "error");
    } finally {
      setBusy(false);
    }
  };

  if (loadError) return <Callout tone="danger" className="tw-mt-4">Couldn't load the website payments setting.</Callout>;
  if (!cfg) return <div className="tw-mt-4"><Shimmer height={52} count={2} /></div>;

  const ownGateway = cfg.paymentType !== "lms_payment";
  const canTurnOn = cfg.planOk && cfg.gatewayReady;

  return (
    <AppCard className="tw-mt-4">
      <AppCardHeader>
        <AppCardTitle>
          <span className="tw-inline-flex tw-items-center tw-gap-2">
            <Globe size={18} aria-hidden="true" /> Payments on your website
          </span>
        </AppCardTitle>
        <AppCardDescription>
          Visitors pay for a course on its page on your website and are enrolled straight away — the payment is
          recorded in Fees with a receipt, and they get their login details by email. For a live course with a
          registration fee they can pay just that first. Every order is in{" "}
          <Link to="/website-orders" className="tw-text-primary">Website orders</Link>.
        </AppCardDescription>
      </AppCardHeader>
      <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
        <Switch
          checked={cfg.enabled}
          onChange={toggle}
          disabled={busy || (!cfg.enabled && !canTurnOn)}
          label="Take payments on your website"
          description={cfg.enabled
            ? "On. A course shows its Pay button once “Take payments on your website” is ticked in that course's settings."
            : "Off — your website only takes enquiries."}
        />

        {!cfg.planOk && (
          <Callout tone="warning">Your plan does not include website payments. Contact us to add it.</Callout>
        )}

        {cfg.planOk && !cfg.gatewayReady && (
          <Callout
            tone="warning"
            action={<AppButton variant="secondary" size="xs" onClick={load}>Check again</AppButton>}
          >
            {ownGateway
              ? "Add and enable your Razorpay keys above first — payments go straight into your Razorpay account."
              : "Online payments are not available on your account yet. Contact support."}
          </Callout>
        )}

        {cfg.gatewayReady && ownGateway && cfg.mode === "test" && (
          <Callout tone="warning">
            Razorpay is in <strong>test mode</strong>: test cards work and no real money moves. Switch Razorpay to live
            mode before you announce payments on your website.
          </Callout>
        )}

        {cfg.customDomainBlocked && (
          <Callout tone="warning">
            Your website is on your own domain ({cfg.customDomain}), and payments collected through LMS Campus cannot
            open there — Razorpay only accepts payments from the websites registered on the account. Connect your own
            Razorpay account to take payments on your website.
          </Callout>
        )}

        {cfg.gatewayReady && ownGateway && (
          <Callout tone="info">
            Razorpay accepts a payment only from websites listed on your Razorpay account. Make sure your website
            {cfg.customDomain ? <> (<strong>{cfg.customDomain}</strong>)</> : null} is listed in Razorpay Dashboard → Account
            &amp; Settings → Website and app details. For the fastest confirmations, add a webhook there for{" "}
            <code className="tw-text-xs tw-break-all">{webhookUrl()}</code> with the events <em>payment.captured</em> and{" "}
            <em>payment.failed</em> — payments are also checked automatically every few minutes.
          </Callout>
        )}
      </AppCardContent>
    </AppCard>
  );
}
